/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { APPS } from "@buddieshr/components/brandLogos";
import { postDemoForm } from "@buddieshr/utils/api";
import { gtagEvent } from "@buddieshr/utils/gtag";
import { buildTracking } from "@buddieshr/utils/tracking";

const SIZES = ['1–50', '51–100', '101–500', '501–1000', '1000+'];

const DIAL_CODES = [
  { code: 'US', flag: '🇺🇸', dial: '+1', ph: '(555) 123-4567' },
  { code: 'CA', flag: '🇨🇦', dial: '+1', ph: '(506) 234-5678' },
  { code: 'GB', flag: '🇬🇧', dial: '+44', ph: '07700 900123' },
  { code: 'FR', flag: '🇫🇷', dial: '+33', ph: '06 12 34 56 78' },
  { code: 'DE', flag: '🇩🇪', dial: '+49', ph: '0151 23456789' },
  { code: 'ES', flag: '🇪🇸', dial: '+34', ph: '612 34 56 78' },
  { code: 'IT', flag: '🇮🇹', dial: '+39', ph: '312 345 6789' },
  { code: 'NL', flag: '🇳🇱', dial: '+31', ph: '06 12345678' },
  { code: 'IE', flag: '🇮🇪', dial: '+353', ph: '085 123 4567' },
  { code: 'CH', flag: '🇨🇭', dial: '+41', ph: '078 123 45 67' },
  { code: 'SE', flag: '🇸🇪', dial: '+46', ph: '070 123 45 67' },
  { code: 'AU', flag: '🇦🇺', dial: '+61', ph: '0412 345 678' },
  { code: 'IN', flag: '🇮🇳', dial: '+91', ph: '98765 43210' },
  { code: 'SG', flag: '🇸🇬', dial: '+65', ph: '8123 4567' },
  { code: 'BR', flag: '🇧🇷', dial: '+55', ph: '(11) 91234-5678' },
];

// "How did you hear about us?" — same options and values as
// WORKSPACE_DISCOVERY_SOURCE_OPTIONS in buddieshr-webapp-api (app/constants.ts),
// which the webapp and Teams ask from, so demo leads report into the same
// buckets. Keep the values in sync.
const DISCOVERY_SOURCE_OTHER = 'Somewhere else';
const DISCOVERY_SOURCES = [
  { value: 'Someone recommended it', emoji: '💬' },
  { value: 'I used it in my previous company', emoji: '💼' },
  { value: 'Google search', icons: ['/img/redesign/google-logo.png'] },
  { value: 'Reddit', icons: ['/img/redesign/reddit-logo.png'] },
  { value: 'I saw one of the founders on LinkedIn', note: 'JY or Fab', icons: ['/img/redesign/linkedin-logo.png'] },
  { value: 'Slack or Teams app directory', icons: ['/img/redesign/slack-logo.png', '/img/redesign/teams-logo.png'] },
  { value: 'ChatGPT, Claude or another AI', emoji: '✨' },
  { value: 'A review site', emoji: '⭐' },
  { value: DISCOVERY_SOURCE_OTHER, emoji: '🙋' },
];
const DISCOVERY_SOURCE_MAX_LENGTH = 500;

// Prefills picked by /demo?ref=<key>, so a CTA can open the form already
// pointed at the right app and reason. Only fills fields the visitor left empty.
const PREFILLS = {
  'donut-migration': {
    apps: ['alfy'],
    notes: "We're switching from Donut and would like help setting up Alfy, checking our channels, and planning the timing around our Donut renewal.",
  },
};

// ─── Demo page ───
export function DemoPage() {
  const [form, setForm] = useState({
    firstName: '', lastName: '',
    email: '', dialCode: '+1', phone: '', company: '', role: '',
    size: '',
    platform: '',
    apps: new Set(),
    suite: false,
    timing: '',
    discoverySource: '',
    discoverySourceOther: '',
    notes: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const router = useRouter();
  const prefill = router.isReady ? PREFILLS[router.query.ref] : null;

  useEffect(() => {
    if (!prefill) return;
    setForm(f => ({
      ...f,
      apps: f.apps.size > 0 ? f.apps : new Set(prefill.apps),
      notes: f.notes || prefill.notes,
    }));
  }, [prefill]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleApp = (id) => {
    setForm(f => {
      const next = new Set(f.apps);
      next.has(id) ? next.delete(id) : next.add(id);
      return { ...f, apps: next, suite: false };
    });
  };
  const pickSuite = () => {
    setForm(f => {
      const all = new Set(APPS.map(a => a.id));
      return { ...f, apps: all, suite: !f.suite ? true : false };
    });
  };

  const valid = form.firstName.trim() && form.email.includes('@') && form.email.includes('.') && form.phone.trim() && form.company.trim() && form.size;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);

    const payload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      phone: `${form.dialCode} ${form.phone}`.trim(),
      company: form.company.trim(),
      role: form.role,
      size: form.size,
      platform: form.platform,
      apps: [...form.apps],
      suite: form.suite,
      timing: form.timing,
      discoverySource: form.discoverySource === DISCOVERY_SOURCE_OTHER
        ? form.discoverySourceOther.trim()
        : form.discoverySource,
      notes: form.notes.trim(),
      ...buildTracking(),
    };

    try {
      gtagEvent({
        action: 'demo_form_submit',
        category: 'register',
        label: 'DemoForm',
      });
      await postDemoForm(payload);
    } catch (error) {
      gtagEvent({
        action: 'demo_form_submit_error',
        category: 'register',
        label: 'DemoForm',
      });
    } finally {
      setSubmitted(true);
      setSubmitting(false);
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  if (submitted) return <Success form={form} />;

  return (
    <div style={{ background: 'var(--bh-cream)' }}>
      <main className="bh-demo-grid" style={{ maxWidth: 1100, margin: '0 auto', padding: '36px 40px 100px', display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 56, alignItems: 'start' }}>
        {/* ─── Left: pitch ─── */}
        <aside style={{ position: 'sticky', top: 36 }}>
          <div className="bh-chip" style={{ marginBottom: 18 }}>📅 15 min · No slides</div>
          <h1 style={{ fontFamily: 'var(--bh-font-display)', fontSize: 'clamp(36px, 4.4vw, 56px)', fontWeight: 500, letterSpacing: '-0.035em', lineHeight: 1, margin: 0 }}>
            See it live.<br/>
            <span style={{ fontStyle: 'italic', color: 'var(--bh-coral-400)' }}>Then decide.</span>
          </h1>
          <p style={{ fontSize: 17, color: 'var(--bh-ink-soft)', marginTop: 18, lineHeight: 1.55, maxWidth: 380 }}>
            We'll walk you through the apps that matter to you, and answer pricing questions on the spot.
          </p>

          <ul style={{ marginTop: 28, padding: 0, listStyle: 'none', display: 'grid', gap: 12 }}>
            {[
              ['📞', 'A real product walkthrough, not a slide deck.'],
              ['💬', 'Pricing, security, deployment, answered live.'],
              ['🚪', 'No follow-up sequence if it’s not a fit.'],
            ].map(([emoji, t]) => (
              <li key={t} style={{ display: 'flex', gap: 12, fontSize: 14.5, color: 'var(--bh-ink-soft)', lineHeight: 1.4 }}>
                <span style={{ fontSize: 18, lineHeight: 1.2 }}>{emoji}</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>

          <div style={{
            marginTop: 32,
            background: '#fff',
            border: '1.5px solid var(--bh-line)',
            borderRadius: 18,
            padding: 20,
          }}>
            <div style={{ fontSize: 13.5, color: 'var(--bh-ink-mute)', fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase', marginBottom: 8 }}>Or skip the call</div>
            <div style={{ fontSize: 14.5, color: 'var(--bh-ink-soft)', lineHeight: 1.5, marginBottom: 14 }}>
              Public pricing, instant install, free under 10 employees. You can be using it before you'd even be on this call.
            </div>
            <Link href="/get-started" style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 14, fontWeight: 600, color: 'var(--bh-ink)',
              textDecoration: 'underline', textDecorationColor: 'var(--bh-sun-400)', textUnderlineOffset: 4, textDecorationThickness: 2,
            }}>
              Start free in Slack <span className="bh-arrow">→</span>
            </Link>
          </div>
        </aside>

        {/* ─── Right: form ─── */}
        <form onSubmit={submit} style={{
          background: '#fff',
          border: '1.5px solid var(--bh-line)',
          borderRadius: 22,
          padding: '36px 40px',
          boxShadow: 'var(--bh-shadow-sm)',
          display: 'flex', flexDirection: 'column', gap: 22,
        }}>
          <div>
            <h2 style={{ fontFamily: 'var(--bh-font-display)', fontSize: 26, fontWeight: 500, letterSpacing: '-0.025em', margin: 0 }}>Tell us a bit about you.</h2>
            <p style={{ marginTop: 6, fontSize: 14, color: 'var(--bh-ink-mute)' }}>Takes ~30 seconds.</p>
          </div>

          <div className="row-2">
            <div className="field">
              <label className="label">First name<span className="req">*</span></label>
              <input className="input" placeholder="Jamie" value={form.firstName} onChange={e => set('firstName', e.target.value)} required />
            </div>
            <div className="field">
              <label className="label">Last name</label>
              <input className="input" placeholder="Rivera" value={form.lastName} onChange={e => set('lastName', e.target.value)} />
            </div>
          </div>

          <div className="field">
            <label className="label">Work email<span className="req">*</span></label>
            <input className="input" type="email" placeholder="jamie@yourcompany.com" value={form.email} onChange={e => set('email', e.target.value)} required />
            <div className="hint">Use your company email, we don't accept personal addresses for demos.</div>
          </div>

          <div className="field">
            <label className="label">Phone number<span className="req">*</span></label>
            <div style={{ display: 'flex', gap: 8 }}>
              <select
                className="select"
                value={form.dialCode}
                onChange={e => set('dialCode', e.target.value)}
                style={{ flex: '0 0 140px' }}
              >
                {DIAL_CODES.map(c => (
                  <option key={c.code + c.dial} value={c.dial}>{c.flag} {c.code} {c.dial}</option>
                ))}
              </select>
              <input className="input" type="tel" placeholder={(DIAL_CODES.find(c => c.dial === form.dialCode) || DIAL_CODES[0]).ph} value={form.phone} onChange={e => set('phone', e.target.value)} required style={{ flex: 1 }} />
            </div>
          </div>

          <div className="row-2">
            <div className="field">
              <label className="label">Company name<span className="req">*</span></label>
              <input className="input" placeholder="Acme Inc." value={form.company} onChange={e => set('company', e.target.value)} required />
            </div>
            <div className="field">
              <label className="label">Your role<span className="req">*</span></label>
              <select className="select" value={form.role} onChange={e => set('role', e.target.value)} required>
                <option value="">Select…</option>
                <option>Head / VP of People</option>
                <option>HR Manager</option>
                <option>People Ops</option>
                <option>Founder / CEO</option>
                <option>Office Manager</option>
                <option>IT / Workspace admin</option>
                <option>Other</option>
              </select>
            </div>
          </div>

          <div className="field">
            <label className="label">Company size<span className="req">*</span></label>
            <div className="chip-row">
              {SIZES.map(s => (
                <button
                  key={s} type="button"
                  className={`chip-pick ${form.size === s ? 'active' : ''}`}
                  onClick={() => set('size', s)}
                >
                  {s}
                </button>
              ))}
            </div>
            <div className="hint">Free forever under 10 employees, you may not even need a demo.</div>
          </div>

          <div className="field">
            <label className="label">Where does your team work?</label>
            <div className="chip-row">
              {[
                { id: 'slack', label: 'Slack', icons: ['/img/redesign/slack-logo.png'] },
                { id: 'teams', label: 'Microsoft Teams', icons: ['/img/redesign/teams-logo.png'] },
                { id: 'both',  label: 'Both', icons: ['/img/redesign/slack-logo.png', '/img/redesign/teams-logo.png'] },
                { id: 'neither', label: 'Neither / unsure' },
              ].map(o => (
                <button
                  key={o.id} type="button"
                  className={`chip-pick ${form.platform === o.id ? 'active' : ''}`}
                  onClick={() => set('platform', o.id)}
                >
                  {o.icons && o.icons.map((src, i) => (
                    <img
                      key={i}
                      src={src}
                      alt=""
                      style={{ width: 16, height: 16, marginRight: i === o.icons.length - 1 ? 8 : 4, verticalAlign: '-3px' }}
                    />
                  ))}
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          <div className="field">
            <label className="label">Which apps are you most curious about?</label>
            <div className="hint" style={{ marginBottom: 8 }}>Select any. We'll focus the demo there.</div>

            <div
              className={`suite-pick ${form.suite ? 'active' : ''}`}
              onClick={pickSuite}
              role="button"
            >
              <div style={{ fontSize: 26, lineHeight: 1 }}>🌟</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 700 }}>Show me everything</div>
                <div className="suite-sub" style={{ fontSize: 12.5, color: 'var(--bh-ink-mute)', marginTop: 2 }}>
                  The whole suite, $2/user/month. Or $3 with SimplePerf included.
                </div>
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, opacity: form.suite ? 1 : .55 }}>
                {form.suite ? '✓ Selected' : 'Pick all'}
              </div>
            </div>

            <div className="apps-grid">
              {APPS.map(app => {
                const Logo = app.Logo;
                const active = form.apps.has(app.id);
                return (
                  <div
                    key={app.id}
                    className={`app-card ${active ? 'active' : ''}`}
                    onClick={() => toggleApp(app.id)}
                    role="checkbox"
                    aria-checked={active}
                  >
                    <div className="check">✓</div>
                    <div className="app-icon-wrap"><Logo /></div>
                    <div className="app-name">{app.name}</div>
                    <div className="app-tag">{app.tagline}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="field">
            <label className="label">When are you looking to roll out?</label>
            <select className="select" value={form.timing} onChange={e => set('timing', e.target.value)}>
              <option value="">Select…</option>
              <option>This month</option>
              <option>Next 1–3 months</option>
              <option>Later this quarter</option>
              <option>Just exploring</option>
            </select>
          </div>

          <div className="field">
            <label className="label">How did you hear about us?</label>
            <div className="chip-row" role="radiogroup" aria-label="How did you hear about us?">
              {DISCOVERY_SOURCES.map(o => {
                const active = form.discoverySource === o.value;
                return (
                  <button
                    key={o.value} type="button"
                    role="radio" aria-checked={active}
                    className={`chip-pick ${active ? 'active' : ''}`}
                    onClick={() => set('discoverySource', active ? '' : o.value)}
                  >
                    {o.icons ? o.icons.map((src, i) => (
                      <img
                        key={src}
                        src={src}
                        alt=""
                        style={{ width: 16, height: 16, marginRight: i === o.icons.length - 1 ? 8 : 4, verticalAlign: '-3px' }}
                      />
                    )) : <span style={{ marginRight: 6 }}>{o.emoji}</span>}
                    {o.value}
                    {o.note && <span style={{ marginLeft: 4, opacity: .6 }}>({o.note})</span>}
                  </button>
                );
              })}
            </div>
            {form.discoverySource === DISCOVERY_SOURCE_OTHER && (
              <input
                className="input"
                autoFocus
                style={{ marginTop: 10 }}
                maxLength={DISCOVERY_SOURCE_MAX_LENGTH}
                placeholder="Tell us where: a newsletter, an event, a podcast…"
                value={form.discoverySourceOther}
                onChange={e => set('discoverySourceOther', e.target.value)}
              />
            )}
          </div>

          <div className="field">
            <label className="label">Anything else? <span style={{ fontWeight: 400, color: 'var(--bh-ink-mute)' }}>(optional)</span></label>
            <textarea className="textarea" placeholder="e.g. We're moving off Bonusly, comparing 15Five, want to gauge eNPS quarterly…" value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>

          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 14 }}>
            <div style={{ fontSize: 12.5, color: 'var(--bh-ink-mute)', maxWidth: 360, lineHeight: 1.5 }}>
              By submitting, you agree to receive a single follow-up email. We don't sell or share your data. <Link href="/privacy" style={{ color: 'inherit' }}>Privacy</Link>.
            </div>
            <button type="submit" className="bh-btn primary" disabled={!valid || submitting} style={{
              opacity: valid && !submitting ? 1 : .5,
              cursor: valid && !submitting ? 'pointer' : 'not-allowed',
            }}>
              {submitting ? 'Sending…' : <>Request my demo <span className="bh-arrow">→</span></>}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}

// ─── Success state ───
function Success({ form }) {
  return (
    <div style={{ background: 'var(--bh-cream)', display: 'flex', flexDirection: 'column' }}>
      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 40px 100px', textAlign: 'center', maxWidth: 720, margin: '0 auto' }}>
        <div style={{ fontSize: 64, lineHeight: 1, marginBottom: 20 }}>🎉</div>
        <div className="bh-chip" style={{ marginBottom: 18 }}>✓ We got it</div>
        <h1 style={{ fontFamily: 'var(--bh-font-display)', fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 500, letterSpacing: '-0.035em', lineHeight: 1.05, margin: 0 }}>
          Thanks {form.firstName}, <span style={{ fontStyle: 'italic', color: 'var(--bh-coral-400)' }}>see you soon.</span>
        </h1>
        <p style={{ fontSize: 17, color: 'var(--bh-ink-soft)', marginTop: 16, lineHeight: 1.55, maxWidth: 520 }}>
          We'll reach out to <strong style={{ color: 'var(--bh-ink)' }}>{form.email}</strong> as soon as possible to set up your demo.
        </p>
        <div style={{ marginTop: 30, display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Link href="/get-started" className="bh-btn primary">◆ Or, just start free <span className="bh-arrow">→</span></Link>
          <Link href="/" className="bh-btn ghost">Back to home</Link>
        </div>
        <div style={{ marginTop: 48, padding: '20px 24px', background: '#fff', border: '1.5px solid var(--bh-line)', borderRadius: 16, maxWidth: 480, textAlign: 'left' }}>
          <div style={{ fontSize: 12.5, color: 'var(--bh-ink-mute)', fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 6 }}>What you told us</div>
          <div style={{ fontSize: 14, color: 'var(--bh-ink-soft)', lineHeight: 1.6 }}>
            <strong>{form.company}</strong> · {form.size} employees{form.platform ? ` · works in ${form.platform}` : ''}
            {form.apps.size > 0 && (
              <div style={{ marginTop: 6 }}>
                Curious about: <strong style={{ color: 'var(--bh-ink)' }}>{form.suite ? 'the whole Culture OS suite' : [...form.apps].map(id => APPS.find(a => a.id === id)?.name).join(', ')}</strong>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default DemoPage;
